import { useState } from "react";
import dayjs from "dayjs";
import {
  Button,
  TabbedDrawer,
  TabbedDrawerHeader,
  TabbedDrawerTitle,
  TabbedDrawerTab,
  TabbedDrawerCTA,
} from "@sellar-io/components";
import {
  FinanceDrawerCreditNote,
  FinanceDrawerSellarCreditInvoice,
} from "./FinanceDrawer";
import { type SellarCreditDocument } from "./PayoutTable.shared";
import { useOrderDocumentDownload } from "../../../../hooks/api/v2/orderDocument/useOrderDocumentDownload";
import { useToast } from "../../../../components/toast/useToast.shared";
import { Toast } from "../../../../components/toast/Toast";
import { downloadPDF } from "../../../../utils/downloadHelpers";
import {
  OrderDocumentListResponseType,
  InvoicePayoutType,
  CreditNotePayoutType,
  InvoiceNextPayoutType,
} from "@sellar-io/api-v2/types";
import { capitalize } from "@sellar-io/common";

enum InvoiceDrawerTabs {
  SUMMARY,
}

type OrderDocument = OrderDocumentListResponseType[number];

export type DocumentType = "invoice" | "credit-note" | "fee-invoice";

export const SellarCreditDrawer = ({
  active,
  setActive,
  document,
}: {
  active: boolean;
  setActive: (value: boolean) => void;
  document?: SellarCreditDocument;
}) => {
  const [downloadingDocumentId, setDownloadingDocumentId] = useState<string>();
  const orderDocumentDownloadMutation = useOrderDocumentDownload();
  const { toast } = useToast();

  if (!document) return null;

  const serverType = (() => {
    switch (document.type) {
      case "invoice":
        return "invoice" as const;
      case "credit-note":
        return "credit-note" as const;
      case "fee":
        return "fee-invoice" as const;
      default:
        return null;
    }
  })();

  const getPaidAtValue = (
    docType: DocumentType,
    contents: InvoicePayoutType | CreditNotePayoutType | InvoiceNextPayoutType,
  ): Date | null => {
    if (docType === "credit-note") {
      return null; // credit notes don't have paidAt
    }
    // docType === "invoice" => we have an InvoicePayoutType
    const invoice = contents as InvoicePayoutType;
    return invoice.paidAt ?? null;
  };

  function handleDownloadDocument(docType: DocumentType) {
    if (!document) return;
    const paidAt = getPaidAtValue(docType, document.contents);

    // we don't need PayoutId here as we are coming from the payout page
    const serverDoc: Omit<OrderDocument, "PayoutId"> = {
      ...document.contents,
      id: document.contents.id || 0,
      type: docType,
      issuedAt: document.contents.issuedAt || null,
      paidAt,
      voidedAt: null,
      // Use an empty string if no customId
      customId: document.contents.customId || "",
    };

    setDownloadingDocumentId(`${serverDoc.type}-${serverDoc.id}`);

    orderDocumentDownloadMutation.mutate(
      { id: serverDoc.id, type: serverDoc.type },
      {
        onSuccess: (data) => {
          const documentName =
            serverDoc.type === "credit-note" ? "Credit note" : "Invoice";

          const documentFileName = `${documentName} - ${
            serverDoc.customId
          } - ${dayjs(serverDoc.issuedAt ?? new Date()).format(
            "DD-MM-YYYY",
          )}.pdf`;

          downloadPDF(documentFileName, data.document);
          setDownloadingDocumentId("");
        },
        onError: () => {
          toast({
            placement: "bottom",
            render: () => (
              <Toast icon="ALERT_FILLED">
                {`Unable to download ${
                  serverDoc.type === "invoice" ? "invoice" : "credit note"
                } at this time`}
              </Toast>
            ),
          });
          setDownloadingDocumentId("");
        },
      },
    );
  }

  return (
    <TabbedDrawer
      active={active}
      setActive={setActive}
      initTab={InvoiceDrawerTabs.SUMMARY}
    >
      <TabbedDrawerHeader>
        <TabbedDrawerTitle
          title={
            document.type === "credit-note"
              ? `Credit Note # ${document.contents.customId || ""}`
              : document.type === "invoice"
                ? `Invoice # ${document.contents.customId || ""}`
                : `Fee # ${document.contents.customId || ""}`
          }
          subtitle={capitalize(
            document.contents.status?.replace("_", " ") || "",
          )}
          imageUrl={
            document.contents.status === "payment_processing"
              ? "https://storage.googleapis.com/sellar-partner-images/sellar-image-assets/app-assets/payout-processing.svg"
              : undefined
          }
        />
      </TabbedDrawerHeader>

      <TabbedDrawerTab tab={InvoiceDrawerTabs.SUMMARY}>
        {document.type === "credit-note" ? (
          <FinanceDrawerCreditNote
            creditNote={document.contents as CreditNotePayoutType}
          />
        ) : (
          <FinanceDrawerSellarCreditInvoice
            invoice={document.contents as InvoicePayoutType}
          />
        )}
      </TabbedDrawerTab>

      <TabbedDrawerCTA>
        {/* Hide if it's a fee */}
        {serverType && (
          <Button
            className="w-full"
            onClick={() => handleDownloadDocument(serverType)}
            disabled={!!downloadingDocumentId}
          >
            {serverType === "credit-note"
              ? "Download Credit Note"
              : "Download Invoice"}
          </Button>
        )}
      </TabbedDrawerCTA>
    </TabbedDrawer>
  );
};
